import { Link } from 'react-router-dom';
import type { AdminPackageDetail } from '../../types/packages';
import { formatAed, formatInstant } from '../offers/offerReviewModel';
import PackageStatusChip from './PackageStatusChip';
import { formatPackageWindow, modeLabel, unavailableReasonLabel } from './packageReviewModel';

export interface PackageFactsPanelProps {
  detail: AdminPackageDetail;
}

/** Spec 081b — the numbers the admin decides on, read straight from `AdminPackageDetail`. */
export default function PackageFactsPanel(props: PackageFactsPanelProps) {
  const { detail } = props;
  const fixed = detail.pricingMode === 'FIXED';
  const lead = detail.items[0];
  const priorRejections = detail.history?.priorRejections;

  return (
    <div className="admin-card offer-facts" data-testid="package-facts">
      <div className="admin-card-header">
        <h3 className="admin-card-title">Facts</h3>
        <PackageStatusChip status={detail.status} />
      </div>

      <dl className="offer-facts__list">
        <dt>Garage</dt>
        <dd><Link to={`/garages/${detail.garage.id}`} className="admin-link">{detail.garage.name}</Link></dd>

        <dt>Pricing</dt>
        <dd>{modeLabel(detail)}</dd>

        <dt>{fixed ? 'Original total' : 'Lowest total'}</dt>
        <dd data-testid="package-facts-original"><s>{formatAed(fixed ? detail.originalTotal : (detail.fromTotal ?? detail.originalTotal))}</s></dd>

        <dt>{fixed ? 'Package price' : 'Discount'}</dt>
        <dd data-testid="package-facts-price" style={{ fontWeight: 600 }}>
          {fixed ? formatAed(detail.packagePrice) : `${detail.discountPercent ?? '—'}% (from ${formatAed(detail.fromPrice)})`}
        </dd>

        <dt>Saving</dt>
        <dd data-testid="package-facts-saving">
          {detail.savingAmount != null ? formatAed(detail.savingAmount) : '—'}
          {detail.savingPercent != null ? ` (${detail.savingPercent}%)` : ''}
        </dd>

        <dt>Window</dt>
        <dd>{formatPackageWindow(detail.startsAt, detail.endsAt)}</dd>

        <dt>Lead service</dt>
        <dd data-testid="package-facts-lead">{lead ? lead.name : '—'}</dd>

        {detail.durationMinutes ? (<><dt>Duration</dt><dd>{detail.durationMinutes} min</dd></>) : null}

        <dt>Submitted</dt>
        <dd>{formatInstant(detail.submittedAt)}</dd>

        {priorRejections != null && (
          <>
            <dt>Prior rejections</dt>
            <dd>{priorRejections > 0 ? <span className="admin-badge rejected">{priorRejections}</span> : '0'}</dd>
          </>
        )}
        {detail.rejectionReason && (<><dt>Last rejection</dt><dd>{detail.rejectionReason}</dd></>)}
        {detail.suspendReason && (<><dt>Suspended for</dt><dd>{detail.suspendReason}</dd></>)}
        {detail.unavailableReason && (
          <>
            <dt>Unavailable</dt>
            <dd><span className="admin-badge warning">{unavailableReasonLabel(detail.unavailableReason)}</span> since {formatInstant(detail.unavailableSince)}</dd>
          </>
        )}
        {detail.lastEditedAt && (
          <>
            <dt>Last edited</dt>
            <dd>{formatInstant(detail.lastEditedAt)}{detail.lastEditedBy ? ` by ${detail.lastEditedBy === 'ADMIN' ? 'an admin' : 'the garage'}` : ''}</dd>
          </>
        )}
      </dl>

      <h4 className="offer-facts__subhead">Services ({detail.items.length})</h4>
      <table className="admin-table offer-facts__items">
        <thead>
          <tr>
            <th>Service</th>
            <th>Choices</th>
            <th>List price</th>
          </tr>
        </thead>
        <tbody>
          {detail.items.map((item, idx) => (
            <tr key={item.offeringId} data-testid="package-facts-item">
              <td>
                {item.name}
                {idx === 0 && <span className="admin-badge neutral" style={{ marginInlineStart: 6 }}>Lead</span>}
                {item.isCarSpecific && <span className="admin-badge order-info" style={{ marginInlineStart: 6 }}>Car-specific</span>}
                {!item.eligible && (
                  <span className="admin-badge warning" style={{ marginInlineStart: 6 }}>{item.ineligibleReason || 'Not eligible'}</span>
                )}
              </td>
              <td className="package-facts__choices">
                {item.fixedChoices.map((c) => `${c.variationName}: ${c.label}`).join(' · ')}
                {item.openVariations.map((v) => (
                  <div key={v.variationId} className="admin-text-muted">Customer picks {v.name}</div>
                ))}
                {item.fixedChoices.length === 0 && item.openVariations.length === 0 && <span className="admin-text-muted">—</span>}
              </td>
              <td>{fixed ? '' : 'from '}{formatAed(item.listPrice)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2} style={{ fontWeight: 600 }}>{fixed ? 'Original total' : 'Lowest total'}</td>
            <td style={{ fontWeight: 600 }}>{formatAed(fixed ? detail.originalTotal : (detail.fromTotal ?? detail.originalTotal))}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
